import {useEffect, useRef} from 'react';
import {useListContext, usePermissions} from 'react-admin';
import {useScope} from './ScopeContext';

/**
 * Seeds the list's filterValues with scope (umbrella_id, charity_id) when the
 * list opts in via `mode`.
 *
 * mode:
 *   - true             → apply both umbrella_id and charity_id (numeric ids)
 *   - 'umbrellaOnly'   → apply only umbrella_id (numeric id)
 *   - 'v2Children'     → apply umbrella_id (external/UUID, admin only) and
 *                        charity_external_id (external/UUID); any stale numeric
 *                        charity_id / numeric umbrella_id left over from a v1
 *                        list visit is stripped so it can't leak into the v2 call
 *   - 'v2Charities'    → apply umbrella_id (external/UUID, admin only); the
 *                        charities list has no charity-level scope key. Any
 *                        stale numeric umbrella_id left over from a v1 list
 *                        visit is stripped so it can't leak into the v2 call
 *   - 'v2People'       → guardians/volunteers/umbrellaAdmins lists. UUID-valued
 *                        umbrella_id AND charity_id (both keys, unlike v2Children —
 *                        the people endpoints silently ignore charity_external_id,
 *                        so reusing that mode would silently lose scope). admin
 *                        seeds both (when set); umbrellad seeds charity_id only
 *                        (umbrella_id is implicit server-side); any other role
 *                        (charity, volunteer, unknown) seeds neither and strips
 *                        both. Stale numeric residue and a stray charity_external_id
 *                        (never valid here) are stripped on every render.
 *   - 'v2UmbrellaOnly' → umbrellaAdmins list. UUID-valued umbrella_id, admin only;
 *                        charity_id is always stripped (the mode it replaces —
 *                        'umbrellaOnly' — does the same).
 *   - falsy            → no-op
 *
 * Behaviour:
 *   - Scope is authoritative. The current ScopeContext value is force-applied on
 *     every mount and on every scope change, overriding whatever umbrella_id /
 *     charity_id react-admin restored from its per-resource localStorage store
 *     (`RaStore.<resource>.listParams`). Without this, a stale per-resource copy
 *     of a scope key — left over from an earlier visit — would silently win over
 *     the current global scope, so each list "remembered" its own old filter.
 *   - When the global scope is cleared, the scope keys are removed from the list
 *     filter too (so a stale persisted copy can't linger).
 *   - Non-scope filters (search, enabled, saved-view keys, etc.) are untouched.
 *
 * Trade-off: a deep-link URL carrying a *different* scope no longer wins on mount
 * — the global scope always does. Shareable scoped links are not a current need;
 * if they become one, detect a genuine query param on initial page load (before
 * the RA store hydrates) and let it seed the scope once.
 */
const useScopedFilter = (mode) => {
    const {umbrellaId, charityId, umbrellaExternalId, charityExternalId} = useScope();
    const {permissions} = usePermissions();
    const {filterValues, setFilters, displayedFilters} = useListContext();

    // umbrella_id scope is an admin-only concept. Umbrella and charity roles are
    // already scoped to their umbrella server-side by JWT identity, so seeding an
    // umbrella_id here is redundant — and surfaces a confusing "Scoped to Umbrella
    // #N" banner (often from a stale localStorage value left by a prior admin
    // session). The charity sub-scope still applies for them.
    const isAdmin = permissions === 'admin';

    // Always read current filterValues / setters inside the effect.
    const filterValuesRef = useRef(filterValues);
    const setFiltersRef = useRef(setFilters);
    const displayedFiltersRef = useRef(displayedFilters);
    filterValuesRef.current = filterValues;
    setFiltersRef.current = setFilters;
    displayedFiltersRef.current = displayedFilters;

    useEffect(() => {
        if (!mode) return;

        const current = filterValuesRef.current ?? {};

        if (mode === 'v2Children') {
            // v2 children filters use external (UUID) ids under different keys
            // than the numeric v1 filters. Any stale numeric charity_id left
            // behind from before this list opted into v2 must not leak into
            // the v2 API call, so it is always stripped regardless of whether
            // umbrella_id / charity_external_id already match.
            const targetUmbrella = isAdmin ? (umbrellaExternalId ?? null) : null;
            const targetCharity = charityExternalId ?? null;

            const currentUmbrella = current.umbrella_id ?? null;
            const currentCharity = current.charity_external_id ?? null;
            const hasStaleCharityId = Object.prototype.hasOwnProperty.call(current, 'charity_id');

            if (currentUmbrella === targetUmbrella && currentCharity === targetCharity && !hasStaleCharityId) {
                return;
            }

            const next = {...current};
            delete next.charity_id;
            if (targetUmbrella != null) next.umbrella_id = targetUmbrella;
            else delete next.umbrella_id;
            if (targetCharity != null) next.charity_external_id = targetCharity;
            else delete next.charity_external_id;

            setFiltersRef.current(next, displayedFiltersRef.current);
            return;
        }

        if (mode === 'v2Charities') {
            // v2 charities filters use an external (UUID) umbrella_id, unlike
            // the numeric umbrella_id used by v1 filters (same key, different
            // value shape) — a stale numeric value is naturally overwritten by
            // the equality check below since it never matches the UUID target.
            // No charity-level scope key exists on this list, and unlike
            // v2Children there is also no stale `charity_id` to strip — nothing
            // on this list ever writes that key, so there's nothing left behind
            // from a prior v1 visit to clean up.
            const targetUmbrella = isAdmin ? (umbrellaExternalId ?? null) : null;
            const currentUmbrella = current.umbrella_id ?? null;

            if (currentUmbrella === targetUmbrella) {
                return;
            }

            const next = {...current};
            if (targetUmbrella != null) next.umbrella_id = targetUmbrella;
            else delete next.umbrella_id;

            setFiltersRef.current(next, displayedFiltersRef.current);
            return;
        }

        if (mode === 'v2People') {
            // Guardians/volunteers/umbrellaAdmins v2 filters: BOTH umbrella_id and
            // charity_id are UUID-valued (unlike 'v2Children', whose charity-level
            // key is charity_external_id — a key the people endpoints silently
            // ignore, which would otherwise mean a charity-scoped admin's people
            // lists silently lose their scope narrowing).
            let targetUmbrella = null;
            let targetCharity = null;
            if (permissions === 'admin') {
                targetUmbrella = umbrellaExternalId ?? null;
                targetCharity = charityExternalId ?? null;
            } else if (permissions === 'umbrella') {
                // umbrella_id is never seeded for umbrellad — the anchor is implicit
                // server-side, and sending a foreign one 403s (sending your own is
                // pointless).
                targetCharity = charityExternalId ?? null;
            }
            // Any other role (charity, volunteer, unknown): seed nothing — both
            // keys stay null and are stripped below, even though the ScopeSwitcher
            // self-heal (which runs regardless of role) can leave a charity role
            // holding a backfilled charityExternalId — charitad + a scope param is
            // a 400 (§5a), so this branch is load-bearing, not belt-and-braces.

            const currentUmbrella = current.umbrella_id ?? null;
            const currentCharity = current.charity_id ?? null;
            // charity_external_id is a DIFFERENT key from this mode's own charity_id,
            // so it is never covered by the equality check above — a literal copy of
            // 'v2Children's early return would skip stripping it and leave it lingering.
            const hasStaleCharityExternalId = Object.prototype.hasOwnProperty.call(current, 'charity_external_id');

            if (currentUmbrella === targetUmbrella && currentCharity === targetCharity && !hasStaleCharityExternalId) {
                return;
            }

            const next = {...current};
            delete next.charity_external_id;
            if (targetUmbrella != null) next.umbrella_id = targetUmbrella;
            else delete next.umbrella_id;
            if (targetCharity != null) next.charity_id = targetCharity;
            else delete next.charity_id;

            setFiltersRef.current(next, displayedFiltersRef.current);
            return;
        }

        if (mode === 'v2UmbrellaOnly') {
            // Replaces the numeric 'umbrellaOnly' mode for the umbrellaAdmins list.
            // umbrella_id is UUID-valued here; charity_id is never applicable and is
            // always stripped, matching the numeric mode's own behavior below.
            const targetUmbrella = isAdmin ? (umbrellaExternalId ?? null) : null;
            const currentUmbrella = current.umbrella_id ?? null;
            const hasStaleCharityId = Object.prototype.hasOwnProperty.call(current, 'charity_id');

            if (currentUmbrella === targetUmbrella && !hasStaleCharityId) {
                return;
            }

            const next = {...current};
            delete next.charity_id;
            if (targetUmbrella != null) next.umbrella_id = targetUmbrella;
            else delete next.umbrella_id;

            setFiltersRef.current(next, displayedFiltersRef.current);
            return;
        }

        const targetUmbrella = isAdmin ? (umbrellaId ?? null) : null;
        const targetCharity = mode === true ? (charityId ?? null) : null;

        const currentUmbrella = current.umbrella_id ?? null;
        const currentCharity = current.charity_id ?? null;

        if (currentUmbrella === targetUmbrella && currentCharity === targetCharity) {
            return;
        }

        const next = {...current};
        if (targetUmbrella != null) next.umbrella_id = targetUmbrella;
        else delete next.umbrella_id;
        if (targetCharity != null) next.charity_id = targetCharity;
        else delete next.charity_id;

        setFiltersRef.current(next, displayedFiltersRef.current);
    }, [mode, umbrellaId, charityId, umbrellaExternalId, charityExternalId, isAdmin, permissions]);
};

export default useScopedFilter;
